/**
 * Page de connexion `/login` — feedback alpha téléphone (item 3).
 *
 * Pour qui : un membre qui a déjà un compte (créé sur un autre
 * appareil, ou invité à rejoindre sa famille) ou qui vient de se
 * déconnecter. Même squelette visuel que l'onboarding (marque, carte,
 * fond crème).
 *
 * Au succès : `loginAndUpdateSession` puis `syncHomesWithServer`
 * (les foyers du compte arrivent) **avant** de naviguer — sinon le
 * guard « pas de foyer → onboarding » claquerait pendant la sync.
 * Si le compte n'a aucun foyer : direction l'onboarding pour en créer
 * un. Lien retour « Créer un foyer » vers l'onboarding.
 */
import { html, type TemplateResult } from 'lit-html';
import styles from '../onboarding/View.module.css';
import { t, type TKey } from '../../i18n/index.js';
import { loginAndUpdateSession } from '../../stores/session.js';
import { syncHomesWithServer } from '../../stores/homes-sync.js';
import { getHomes } from '../../stores/homes.js';
import { ApiError } from '../../api/client.js';
import { bumpUi } from '../../stores/ui.js';
import { navigate } from '../../lib/router.js';
import { nestorMark } from '../../ui/NestorMark.js';

interface LoginState {
  email: string;
  password: string;
  /** Clé i18n de l'erreur affichée (ou `null`). */
  error: string | null;
  submitting: boolean;
}

const state: LoginState = { email: '', password: '', error: null, submitting: false };

function errorKey(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'invalid_credentials') return 'account_err_invalid_credentials';
    if (err.status === 503) return 'account_err_network';
  }
  if (err instanceof TypeError) return 'account_err_network';
  return 'account_err_generic';
}

function onSubmit(e: Event): void {
  e.preventDefault();
  if (state.submitting) return;
  if (!state.email.includes('@') || state.password.length === 0) {
    state.error = 'account_err_invalid_credentials';
    bumpUi();
    return;
  }
  state.error = null;
  state.submitting = true;
  bumpUi();

  void (async (): Promise<void> => {
    try {
      await loginAndUpdateSession({ email: state.email.trim(), password: state.password });
      // Ramène les foyers du compte AVANT de naviguer (guard onboarding).
      await syncHomesWithServer();
      state.email = '';
      state.password = '';
      state.error = null;
      state.submitting = false;
      navigate(getHomes().length > 0 ? '/' : '/onboarding');
    } catch (err) {
      state.error = errorKey(err);
      state.submitting = false;
      bumpUi();
    }
  })();
}

export function renderLoginView(): TemplateResult {
  const onInput =
    (field: 'email' | 'password') =>
    (e: Event): void => {
      state[field] = (e.target as HTMLInputElement).value;
      if (state.error) {
        state.error = null;
        bumpUi();
      }
    };
  return html`<div class=${styles.screen} data-testid="view-login">
    <div class=${styles.inner}>
      <div class=${styles.logo}>${nestorMark({ size: 28 })}</div>
      <div class=${styles.tagline}>${t('onb_tagline')}</div>
      <form class=${styles.card} data-testid="login-card" @submit=${onSubmit}>
        <h2 class=${styles.title}>${t('login_title')}</h2>
        <div class=${styles.sub}>${t('login_sub')}</div>
        <div class=${styles.formGroup}>
          <label class=${styles.formLabel}>${t('account_email_label')}</label>
          <input
            type="email"
            class=${styles.formInput}
            data-testid="login-view-email"
            placeholder="prenom@exemple.com"
            .value=${state.email}
            @input=${onInput('email')}
            autocomplete="email"
          />
        </div>
        <div class=${styles.formGroup}>
          <label class=${styles.formLabel}>${t('account_password_label')}</label>
          <input
            type="password"
            class=${styles.formInput}
            data-testid="login-view-password"
            .value=${state.password}
            @input=${onInput('password')}
            autocomplete="current-password"
          />
        </div>
        <div
          class=${`${styles.errorMsg} ${state.error ? styles.show : ''}`}
          data-testid="login-view-err"
          role="alert"
        >
          ${state.error ? t(state.error as TKey) : ''}
        </div>
        <button
          type="submit"
          class=${styles.btnPrimary}
          ?disabled=${state.submitting}
          data-testid="login-view-submit"
        >
          ${t('account_btn_submit_login')}
        </button>
        <button
          type="button"
          class=${styles.btnBack}
          data-testid="login-view-to-onboarding"
          @click=${(): void => {
            navigate('/onboarding');
          }}
        >
          ${t('login_no_account')}
        </button>
      </form>
      <div class=${styles.footer}>ALPHA 1.0</div>
    </div>
  </div>`;
}

/** @internal pour tests. */
export function _resetLoginForTests(): void {
  state.email = '';
  state.password = '';
  state.error = null;
  state.submitting = false;
}
